import { COLORS } from "../../shared/constants";
import { IconEdit, IconTrash, IconPlus } from "../../shared/icons";
import { useState, useRef, useEffect } from "react";
import * as api from "../../api";

export default // ─── Providers Tab ────────────────────────────────
function ProvidersTab({ providers, onSave, onDelete }) {
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [ocrAvailable, setOcrAvailable] = useState(null);
  const fileRef = useRef(null);

  // Check OCR availability on mount
  useEffect(() => {
    api.ocrStatus().then(r => setOcrAvailable(r.available)).catch(() => setOcrAvailable(false));
  }, []);

  const handleScanFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setScanError(null);
    setScanResult(null);
    try {
      const result = await api.ocrScanReferral(file);
      setScanResult(result);
      // Open form pre-populated with extracted data
      setEditing("SCAN");
    } catch (e) {
      setScanError(e.error || "OCR scan failed.");
    }
    setScanning(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const ProvForm = ({ prov, onDone, prefill }) => {
    const initial = prefill || prov || {};
    const [f, setF] = useState({
      name: initial.name || "",
      practice_name: initial.practice_name || "",
      phone: initial.phone || "",
      fax: initial.fax || "",
      email: initial.email || "",
      address_line1: initial.address_line1 || "",
      address_line2: initial.address_line2 || "",
      city: initial.city || "",
      state: initial.state || "",
      zip: initial.zip || "",
    });
    const inp = (field, ph, w) => (
      <input
        style={{ height: "34px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", width: w || "100%", boxSizing: "border-box" }}
        value={f[field]}
        onChange={(e) => setF((p) => ({ ...p, [field]: e.target.value }))}
        placeholder={ph}
      />
    );
    return (
      <div style={{ padding: "14px", background: COLORS.primaryLight, borderRadius: "8px", marginBottom: "12px" }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: COLORS.primary, marginBottom: "10px" }}>
          {prefill ? "New Provider (from scan)" : prov ? "Edit Provider" : "New Provider"}
        </div>
        {prefill && prefill.confidence && (
          <div style={{
            padding: "6px 10px", borderRadius: "6px", marginBottom: "10px", fontSize: "11px",
            background: prefill.confidence === "high" ? "#E8F8F0" : prefill.confidence === "medium" ? "#FFFBEB" : "#FEF2F2",
            color: prefill.confidence === "high" ? "#166534" : prefill.confidence === "medium" ? "#92400E" : "#991B1B",
            border: `1px solid ${prefill.confidence === "high" ? "#BBF7D0" : prefill.confidence === "medium" ? "#FDE68A" : "#FECACA"}`,
          }}>
            OCR confidence: {prefill.confidence} — review and correct fields before saving
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
          {inp("name", "Provider Name")}{inp("practice_name", "Practice / Clinic Name")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "8px" }}>
          {inp("phone", "Phone")}{inp("fax", "Fax")}{inp("email", "Email")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
          {inp("address_line1", "Address Line 1")}{inp("address_line2", "Address Line 2")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "8px", marginBottom: "10px" }}>
          {inp("city", "City")}{inp("state", "State")}{inp("zip", "ZIP")}
        </div>
        {/* Show raw OCR text for reference */}
        {prefill && prefill.raw_text && (
          <details style={{ marginBottom: "10px" }}>
            <summary style={{ fontSize: "11px", color: COLORS.textSecondary, cursor: "pointer" }}>
              View raw OCR text
            </summary>
            <pre style={{
              marginTop: "6px", padding: "8px 10px", background: "#F9FAFB", border: `1px solid ${COLORS.borderLight}`,
              borderRadius: "6px", fontSize: "11px", color: COLORS.textSecondary, whiteSpace: "pre-wrap",
              wordBreak: "break-word", maxHeight: "150px", overflow: "auto", lineHeight: "1.5",
            }}>
              {prefill.raw_text}
            </pre>
          </details>
        )}
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => { onSave({ ...(prov && !prefill ? prov : {}), ...f }); onDone(); setScanResult(null); }}
            style={{ height: "34px", padding: "0 14px", borderRadius: "6px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
            Save
          </button>
          <button onClick={() => { onDone(); setScanResult(null); }}
            style={{ height: "34px", padding: "0 14px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textSecondary, fontSize: "12px", cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, margin: 0 }}>Referring Providers</h3>
        <div style={{ display: "flex", gap: "8px" }}>
          {ocrAvailable && (
            <>
              <button onClick={() => fileRef.current?.click()} disabled={scanning}
                style={{
                  height: "36px", padding: "0 16px", borderRadius: "8px",
                  border: `1px solid #059669`, background: COLORS.surface,
                  color: "#059669", fontSize: "13px", fontWeight: "600",
                  cursor: scanning ? "wait" : "pointer",
                  opacity: scanning ? 0.7 : 1,
                  display: "flex", alignItems: "center", gap: "4px",
                }}>
                📷 {scanning ? "Scanning..." : "Scan Referral"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleScanFile} style={{ display: "none" }} />
            </>
          )}
          <button onClick={() => setEditing("NEW")}
            style={{ height: "36px", padding: "0 16px", borderRadius: "8px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
            <IconPlus /> Add Provider
          </button>
        </div>
      </div>

      {/* Scan error */}
      {scanError && (
        <div style={{ padding: "10px 14px", borderRadius: "8px", marginBottom: "12px", fontSize: "13px", background: COLORS.dangerLight, color: COLORS.danger, border: `1px solid ${COLORS.dangerBorder}` }}>
          {scanError}
        </div>
      )}

      {/* New form (manual) */}
      {editing === "NEW" && <ProvForm onDone={() => setEditing(null)} />}

      {/* New form (from scan) */}
      {editing === "SCAN" && scanResult && (
        <ProvForm prefill={scanResult} onDone={() => setEditing(null)} />
      )}

      {providers.map((pv) => (
        <div key={pv.id} style={{ padding: "12px 14px", borderBottom: `1px solid ${COLORS.borderLight}` }}>
          {editing === pv.id ? (
            <ProvForm prov={pv} onDone={() => setEditing(null)} />
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "500", color: COLORS.text }}>{pv.name}{pv.practice_name ? ` — ${pv.practice_name}` : ""}</div>
                <div style={{ fontSize: "12px", color: COLORS.textSecondary, marginTop: "2px" }}>
                  {[pv.city, pv.state].filter(Boolean).join(", ")}{pv.phone ? ` · ${pv.phone}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: "4px" }}>
                <button onClick={() => setEditing(pv.id)} style={{ height: "28px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.primary, fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px" }}><IconEdit /> Edit</button>
                {confirmDel === pv.id ? (
                  <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
                    <button onClick={() => { onDelete(pv.id); setConfirmDel(null); }} style={{ height: "28px", padding: "0 8px", borderRadius: "6px", border: "none", background: COLORS.danger, color: "#FFF", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>Yes</button>
                    <button onClick={() => setConfirmDel(null)} style={{ height: "28px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>No</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDel(pv.id)} style={{ height: "28px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.dangerBorder}`, background: COLORS.dangerLight, color: COLORS.danger, fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px" }}><IconTrash /></button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Lookup Table Tab (Coils, Treatment Spots, MT Spots) ─────
